import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { categories, movies } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState('rating');

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const filteredMovies = activeSlug === 'all'
    ? [...movies]
    : movies.filter((m) => m.category === activeSlug);

  const sortedMovies = [...filteredMovies].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'year') return b.year - a.year;
    return 0;
  });

  const pageTitle = currentCategory && activeSlug !== 'all'
    ? `${currentCategory.name} | 花生影院电影分类`
    : '全部电影 | 花生影院电影大全';
  const pageDesc = currentCategory && activeSlug !== 'all'
    ? `花生影院${currentCategory.name}专区，精选高分${currentCategory.name}资源，支持高清在线观看，实时排行推荐。`
    : '花生影院电影大全，涵盖动作片、喜剧片、科幻片、剧情片等全类型电影，高清免费在线观看。';

  const canonicalSlug = activeSlug === 'all' ? 'all' : activeSlug;

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.huahuashengcinema.com/' },
          {
            '@type': 'ListItem',
            position: 2,
            name: currentCategory && activeSlug !== 'all' ? currentCategory.name : '电影分类',
            item: `https://www.huahuashengcinema.com/category/${canonicalSlug}`,
          },
        ],
      },
      {
        '@type': 'WebPage',
        name: pageTitle,
        url: `https://www.huahuashengcinema.com/category/${canonicalSlug}`,
        description: pageDesc,
      },
    ],
  };

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`花生影院,电影分类,${currentCategory ? currentCategory.name : '电影大全'},电影推荐,在线观看`}
        canonicalUrl={`https://www.huahuashengcinema.com/category/${canonicalSlug}`}
        ogType="website"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryJsonLd) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <nav className="px-4 md:px-6 lg:px-10 py-4 border-b border-background-300/20" aria-label="Breadcrumb">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center gap-2 text-xs md:text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-primary-400 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-300 font-medium">电影分类</li>
              {currentCategory && activeSlug !== 'all' && (
                <>
                  <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                  <li className="text-foreground-300 font-medium">{currentCategory.name}</li>
                </>
              )}
            </ol>
          </div>
        </nav>

        <section className="px-4 md:px-6 lg:px-10 py-6 md:py-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-6">
              {currentCategory ? currentCategory.name : '全部电影'}
              <span className="text-base font-normal text-foreground-500 ml-2">
                共 {sortedMovies.length} 部
              </span>
            </h1>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex flex-wrap gap-1.5">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-950'
                        : 'bg-background-200/40 text-foreground-400 hover:text-foreground-100 hover:bg-background-200/60'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                <button
                  onClick={() => setSortBy('rating')}
                  className={`px-4 py-2 rounded-lg text-xs font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    sortBy === 'rating'
                      ? 'bg-accent-500/20 text-accent-400 border border-accent-500/30'
                      : 'bg-background-200/40 text-foreground-400 hover:text-foreground-100'
                  }`}
                >
                  <i className="ri-star-line mr-1"></i>评分最高
                </button>
                <button
                  onClick={() => setSortBy('year')}
                  className={`px-4 py-2 rounded-lg text-xs font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    sortBy === 'year'
                      ? 'bg-accent-500/20 text-accent-400 border border-accent-500/30'
                      : 'bg-background-200/40 text-foreground-400 hover:text-foreground-100'
                  }`}
                >
                  <i className="ri-time-line mr-1"></i>最新发布
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {sortedMovies.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/category/${movie.category}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3">
                    <img
                      src={movie.posterUrl}
                      alt={movie.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                    <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-background-950/70 backdrop-blur-sm text-accent-400 text-xs font-bold flex items-center gap-1">
                      <i className="ri-star-fill text-[10px]"></i>
                      {movie.rating}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-50 truncate">
                        {movie.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-0.5">{movie.year} / {movie.duration}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {sortedMovies.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/40">
                  <i className="ri-movie-2-line text-2xl text-foreground-500"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的电影</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}